import { z } from 'zod';
import { ZIP_CODE_SCHEMA } from '@/utils/constant';

const FILE_CASE_REGEX = /^[a-zA-Z0-9\-/]+$/;

const fileCaseField = z
  .string()
  .nullable()
  .optional()
  .transform((v) => v?.trim() ?? v)
  .refine((v) => !v || (FILE_CASE_REGEX.test(v) && v.length <= 20), {
    message: 'Must be up to 20 alphanumeric characters, dashes, or slashes',
  });

export const HIStep1Schema = z
  .object({
    serviceType: z.literal('HOUSE_INSPECTION'),
    clientId: z.string({ message: 'Client # is required' }).min(1, 'Client # is required'),
    clientName: z.string().optional().nullable(),
    contactId: z.string({ message: 'Client Contact is required' }).min(1, 'Client Contact is required'),
    contactEmail: z.string().optional().nullable(),
    fileNumber: fileCaseField,
    caseNumber: fileCaseField,
    lawyer: z.string().nullable().optional(),
    represents: z.string().nullable().optional(),
    caseName: z.string().nullable().optional(),
    rushType: z.enum(['SAME_DAY', 'NEXT_DAY', 'NONE']).default('NONE'),
    sameDayRushDisplay: z.boolean().optional(),
    nextDayRushDisplay: z.boolean().optional(),
    prePaid: z.boolean().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    const file = data.fileNumber;
    const caseNo = data.caseNumber;
    if (!file && !caseNo) {
      ctx.addIssue({ code: 'custom', message: 'File # is required', path: ['fileNumber'] });
      ctx.addIssue({ code: 'custom', message: 'Case # is required', path: ['caseNumber'] });
    }
  });

export const HIStep2Schema = z.object({
  mortgageCompanyOrPlaintiff: z
    .string({ message: 'Mortgage Co / Plaintiff is required' })
    .trim()
    .min(1, 'Mortgage Co / Plaintiff is required')
    .max(255, 'Mortgage Co / Plaintiff is too long'),
  ownerOrDefendantName: z
    .string({ message: "Owner's Name / Defendant is required" })
    .trim()
    .min(1, "Owner's Name / Defendant is required")
    .max(255, "Owner's Name / Defendant is too long"),
  addressLine1: z
    .string({ message: 'Address Line 1 is required' })
    .trim()
    .min(1, 'Address Line 1 is required')
    .max(100, 'Address Line 1 is too long'),
  addressLine2: z.string().trim().nullable().optional(),
  city: z.string({ message: 'City is required' }).trim().min(1, 'City is required').max(50, 'City is too long'),
  state: z.string({ message: 'State is required' }).trim().min(1, 'State is required'),
  zipCode: ZIP_CODE_SCHEMA,
  // serverId: editable — user selects a server; triggers assign-server call after creation
  // Preprocess empty string → undefined so clearing the search field doesn't trigger uuid validation
  serverId: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z.string().uuid().nullable().optional()
  ),
  // lastServeDate: user input (no rush) or auto-set from rush type; not sent in payload
  lastServeDate: z.date().nullable().optional(),
  // dateAssigned: autofilled display field (today's date) when a server is selected; not sent in payload
  dateAssigned: z.string().nullable().optional(),
  // teamId / userId: form-only; persisted via assign-team-user after create (XOR per request)
  teamId: z.preprocess((val) => (val === '' ? undefined : val), z.string().uuid().nullable().optional()),
  userId: z.preprocess((val) => (val === '' ? undefined : val), z.string().uuid().nullable().optional()),
});

export type HIFormValues = z.infer<typeof HIStep1Schema> & z.infer<typeof HIStep2Schema>;

// ── Edit schemas ────────────────────────────────────────────────────────────

export const HIEditStep1Schema = z.object({
  // Read-only display values (in form state for dropdown scoping, not validated as required)
  clientId: z.string().optional(),
  clientName: z.string().optional().nullable(),
  fileNumber: z.string().optional().nullable(),
  caseNumber: z.string().optional().nullable(),
  caseName: z.string().optional().nullable(),
  workOrderNumber: z.string().optional(),
  // Editable
  contactId: z.string({ message: 'Client Contact is required' }).min(1, 'Client Contact is required'),
  contactEmail: z.string().optional().nullable(),
  lawyer: z.string().nullable().optional(),
  represents: z.string().nullable().optional(),
  rushType: z.enum(['SAME_DAY', 'NEXT_DAY', 'NONE']).default('NONE'),
  sameDayRushDisplay: z.boolean().optional(),
  nextDayRushDisplay: z.boolean().optional(),
  prePaid: z.boolean().optional().nullable(),
});

export const HIEditStep2Schema = z.object({
  mortgageCompanyOrPlaintiff: z
    .string({ message: 'Mortgage Co / Plaintiff is required' })
    .trim()
    .min(1, 'Mortgage Co / Plaintiff is required')
    .max(255, 'Mortgage Co / Plaintiff is too long'),
  ownerOrDefendantName: z
    .string({ message: "Owner's Name / Defendant is required" })
    .trim()
    .min(1, "Owner's Name / Defendant is required")
    .max(255, "Owner's Name / Defendant is too long"),
  addressLine1: z
    .string({ message: 'Address Line 1 is required' })
    .trim()
    .min(1, 'Address Line 1 is required')
    .max(100, 'Address Line 1 is too long'),
  addressLine2: z.string().trim().nullable().optional(),
  city: z
    .string({ message: 'City is required' })
    .trim()
    .min(1, 'City is required')
    .max(50, 'City is too long'),
  state: z.string({ message: 'State is required' }).trim().min(1, 'State is required'),
  zipCode: ZIP_CODE_SCHEMA,
  // Display-only fields (not sent in update payload; team/user via assign-team-user)
  lastServeDate: z.date().nullable().optional(),
  serverId: z.string().nullable().optional(),
  descriptionOfDocuments: z.string().nullable().optional(),
  teamId: z.preprocess((val) => (val === '' ? undefined : val), z.string().uuid().nullable().optional()),
  userId: z.preprocess((val) => (val === '' ? undefined : val), z.string().uuid().nullable().optional()),
});

export type HIEditFormValues = z.infer<typeof HIEditStep1Schema> & z.infer<typeof HIEditStep2Schema>;

export type HIWorkOrderUpdatePayload = {
  contactId: string;
  lawyer?: string | null;
  represents?: string | null;
  mortgageCompanyOrPlaintiff: string;
  propertyAddress: string;
  city: string;
  state: string;
  zipCode: string;
  ownerOrDefendantName: string;
  descriptionOfDocuments?: string | null;
  rushType: 'SAME_DAY' | 'NEXT_DAY' | 'NONE';
};

export type HIWorkOrderCreatePayload = {
  serviceType: 'HOUSE_INSPECTION';
  clientId: string;
  contactId: string;
  fileNumber?: string | null;
  caseNumber?: string | null;
  lawyer?: string | null;
  represents?: string | null;
  caseName?: string | null;
  rushType: 'SAME_DAY' | 'NEXT_DAY' | 'NONE';
  mortgageCompanyOrPlaintiff: string;
  propertyAddress: string;
  city: string;
  state: string;
  zipCode: string;
  ownerOrDefendantName: string;
  descriptionOfDocuments?: string | null;
  linkedToProcessService?: boolean;
};
